import { createSignal, onMount, Show, createEffect, createMemo, type Component as SolidComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useNavigate } from "@solidjs/router";

import Header from "../components/app/Header";
import SelectionBanner from "../components/app/SelectionBanner";
import Sidebar from "../components/dashboard/Sidebar";
import TagsExplorePanel from "../components/explore/TagsExplorePanel";
import CapitanFloatingButton from "../components/assistant/CapitanFloatingButton";

import { setUser } from "../store/userStore";
import { getData, notifySplitPanelResize } from "../utils/global";
import { warn, error as logError, log } from "../utils/console";
import { authManager } from "../utils/authManager";
import { apiEndpoints } from "@config/env";
import { buildLoginRedirectUrl, parseShareIdFromSearch, buildShareUrl, normalizeDashboardUrl } from "../utils/dashboardUrlUtils";
import {
  getShareLifecycle,
  routeMatchesShareLifecycle,
  clearShareUrlLifecycle,
} from "../store/dashboardShareRestoreStore";

import { isCut, hasSelection, selectedEvents, selectedRange, selectedRanges, selectedGroupKeys, isManeuversReportPage } from "../store/selectionStore";
import { selectedFilters } from "../store/filterStore";
import { persistantStore } from "../store/persistantStore";
import { unifiedDataStore } from "../store/unifiedDataStore";

// Global signal to track which components are active
const [activeComponents, setActiveComponents] = createSignal<Set<string>>(new Set());

// Export the setter so components can register themselves
export const registerActiveComponent = (componentType: string) => {
  setActiveComponents((prev) => {
    if (prev.has(componentType)) {
      return prev;
    }
    return new Set([...prev, componentType]);
  });
};

export const unregisterActiveComponent = (componentType: string) => {
  setActiveComponents((prev) => {
    if (!prev.has(componentType)) {
      return prev;
    }
    const newSet = new Set(prev);
    newSet.delete(componentType);
    return newSet;
  });
};
const { projects, setProjects, selectedDatasetId, selectedProjectId, selectedClassName, setSelectedProjectId, setSelectedClassName, selectedMenu, selectedPage, selectedDate, selectedSourceId, tagsExploreRailOpen } = persistantStore;

/** Stable placeholder so the primary slot stays mounted when no page is loaded. */
const EmptyMainView: SolidComponent<Record<string, unknown>> = () => null;

function projectIdInList(list: { project_id?: unknown }[] | undefined, projectId: number): boolean {
  if (!Array.isArray(list) || !Number.isFinite(projectId) || projectId <= 0) return false;
  return list.some((p) => Number(p?.project_id) === projectId);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [fetchMenuTrigger, setFetchMenuTrigger] = createSignal(false);
  const [sidebarInitialized, setSidebarInitialized] = createSignal(false);
  type DashboardComponent = SolidComponent<any>;
  const [component, setComponent] = createSignal<DashboardComponent | null>(null); 
  const [hasExistingDataset, setHasExistingDataset] = createSignal(false);
  // Initialize to true so sidebar shows immediately on first render
  // onMount will handle data fetching in background
  const [initialCheckComplete, setInitialCheckComplete] = createSignal(true);
  const [projectsFetchComplete, setProjectsFetchComplete] = createSignal(false);
  
  // Split view state management - proper SolidJS approach
  const [isSplitView, setIsSplitView] = createSignal(false);
  const [rightComponent, setRightComponent] = createSignal<DashboardComponent | null>(null);
  const [splitViewTitle, setSplitViewTitle] = createSignal('');
  const [leftPanelWidth, setLeftPanelWidth] = createSignal(50); // Percentage
  const [isHoveringSplitView, setIsHoveringSplitView] = createSignal(false); // Track hover state for close button
  
  // Separate signal for right panel menu (Ctrl+click doesn't affect selectedMenu)
  const [rightPanelMenu, setRightPanelMenu] = createSignal(null);
  const [leftPanelMenu, setLeftPanelMenu] = createSignal<string | null>(null);

  const showSelectionBanner = createMemo(() => {
    const hasBannerSelection =
      hasSelection() ||
      isCut() ||
      selectedFilters().length > 0 ||
      selectedEvents().length > 0 ||
      selectedGroupKeys().length > 0 ||
      (selectedRange()?.length ?? 0) > 0 ||
      (selectedRanges()?.length ?? 0) > 0;

    if (!hasBannerSelection) return false;
    if (isSplitView() || isManeuversReportPage()) return true;

    const mapActive = activeComponents().has('map');
    if (mapActive && selectedSourceId() === 0) return false;
    if (mapActive && selectedDate() !== '') return false;
    return true;
  });

  const fetchUserInfo = async () => {
    const controller = new AbortController();
    
    try {
      const response = await authManager.getCurrentUser();
      if (response.success) {
        log('Authentication successful');
        const user_info = response.data;
        setUser(user_info);
      } else {
        log('Authentication failed, redirecting to login');
        navigate(buildLoginRedirectUrl());
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // Request was cancelled
      } else {
        warn('Dashboard: Authentication error, redirecting to login:', error.message);
        navigate(buildLoginRedirectUrl());
      }
    }
  };

  const fetchProjects = async () => {
    const controller = new AbortController();

    try {
      const response = await getData(`${apiEndpoints.app.projects}/type?type=user`, controller.signal);
      const raw = response.success ? response.data : [];
      const data = Array.isArray(raw) ? raw : [];
      setProjects(data);

      const currentId = Number(selectedProjectId());
      if (data.length === 0) {
        setSelectedProjectId(0);
        setSelectedClassName("");
      } else if (!projectIdInList(data, currentId)) {
        setSelectedProjectId(0);
        setSelectedClassName("");
      }
    } catch (error: any) {
      if (error.name === 'AbortError') { /* empty */ 
      } else {
        logError('Error fetching projects:', error);
        setProjects([]);
        setSelectedProjectId(0);
        setSelectedClassName("");
      }
    } finally {
      setProjectsFetchComplete(true);
    }
  };

  const hasProjectInList = () => {
    const list = projects();
    return Array.isArray(list) && list.length > 0;
  };

  const hasValidSelectedProject = () => {
    const list = projects();
    const pid = Number(selectedProjectId());
    return projectIdInList(list, pid);
  };

  createEffect(() => {
    const lifecycle = getShareLifecycle();
    if (!lifecycle) {
      return;
    }

    const route = {
      projectId: Number(selectedProjectId()),
      className: selectedClassName(),
      datasetId: Number(selectedDatasetId()),
      date: selectedDate() || '',
      menu: selectedMenu() || '',
      page: selectedPage() || '',
      sourceId: Number(selectedSourceId()),
    };

    if (!routeMatchesShareLifecycle(route)) {
      clearShareUrlLifecycle();
      normalizeDashboardUrl();
      return;
    }

    const currentShareId = parseShareIdFromSearch();
    if (currentShareId !== lifecycle.shareId && typeof window !== 'undefined') {
      window.history.replaceState(null, '', buildShareUrl(lifecycle.shareId));
    }
  });

  // Events are now loaded automatically when mapdata is loaded
  // No need for separate events loading in Dashboard

  // Left-panel navigation writes into the same primary slot. Opening split does not.
  const updateLeftComponent = (componentToLoad: () => DashboardComponent) => {
    log('updateLeftComponent called, isSplitView:', isSplitView(), 'componentToLoad:', componentToLoad);
    if (isSplitView()) {
      setComponent(() => componentToLoad());
      setLeftPanelMenu(selectedMenu());
      log('Left component updated');
    }
  };

  // The primary page stays mounted. Split view only adds the right-hand slot.
  const openInSplitView = (
    componentToLoad: () => DashboardComponent,
    title: string,
    menuName: string | null = null,
    _leftComponentFactory: (() => DashboardComponent) | null = null,
    options?: { rightPanelWidthPercent?: number }
  ) => {
    setRightComponent(() => componentToLoad());
    setSplitViewTitle(title);
    if (menuName) {
      setRightPanelMenu(menuName);
    }
    if (!isSplitView()) {
      setLeftPanelMenu(selectedMenu());
      setIsSplitView(true);
    }
    if (typeof options?.rightPanelWidthPercent === 'number') {
      const rightPct = Math.min(80, Math.max(20, options.rightPanelWidthPercent));
      setLeftPanelWidth(100 - rightPct);
    }
    // Notify media-container scaling so content in the panel updates after layout
    setTimeout(notifySplitPanelResize, 100);
    setTimeout(notifySplitPanelResize, 450);
  };

  const closeSplitView = () => {
    setIsSplitView(false);
    setRightComponent(null);
    setSplitViewTitle('');
    setRightPanelMenu(null);
    setLeftPanelMenu(null);
    setLeftPanelWidth(50);
  };

  // Drag functionality for resizing panels
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startWidth = leftPanelWidth();
    const container = document.querySelector('.workspace-shell');
    
    if (!container) return;
    
    const handleDrag = (e) => {
      e.preventDefault();
      const containerWidth = container.offsetWidth;
      const deltaX = e.clientX - startX;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newWidth = Math.max(20, Math.min(80, startWidth + deltaPercent));
      
      setLeftPanelWidth(newWidth);
    };
    
    const handleDragEnd = (e) => {
      e.preventDefault();
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Notify media-container scaling so maneuver (and other) pages in split view update scale
      notifySplitPanelResize();
      // Again after transition (0.2s) so final size is applied
      setTimeout(notifySplitPanelResize, 250);
    };
    
    // Set cursor and prevent text selection
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
  };
  
  // Removed selectedEvents effect - now handled by selection layers in map components

  onMount(async () => {
    log('[Dashboard] onMount started');
    
    
    // Check if there's already a selected dataset
    const currentDatasetId = selectedDatasetId();
    if (currentDatasetId && currentDatasetId > 0) {
      setHasExistingDataset(true);
    }
    
    // initialCheckComplete is already true (set in signal initialization)
    // This allows UI to render immediately while data loads in background
    
    log('[Dashboard] Starting background data fetch');
    // Fetch data in background - don't block rendering
    fetchUserInfo().catch((err) => {
      logError('[Dashboard] Error fetching user info:', err);
    });
    
    fetchProjects().catch((err) => {
      logError('[Dashboard] Error fetching projects:', err);
    });

    // Check if cache needs initialization and trigger it
    const { isCacheInitialized, initializeApplicationCache } = persistantStore;
    if (!isCacheInitialized()) {
      log('[Dashboard] Cache not initialized, triggering initialization...');
      initializeApplicationCache().catch((err) => {
        logError('[Dashboard] Error during cache initialization:', err);
      });
    }

    // Validate dataset cache if project and class are available
    // Run this asynchronously in background after a short delay to allow stores to initialize
    setTimeout(async () => {
      const projectId = selectedProjectId();
      const className = selectedClassName();
      if (projectId && projectId > 0 && className) {
        log('[Dashboard] Validating dataset cache on app initialization');
        unifiedDataStore.validateDatasetCache(className, projectId).catch((err) => {
          logError('[Dashboard] Error validating dataset cache:', err);
        });
      }
    }, 1000); // Wait 1 second for stores to initialize
  });
  
  // Watch for dataset changes
  createEffect(() => {
    const currentDatasetId = selectedDatasetId();
    // Dataset changed
  });

  return (
    <>
      <Header />
      <div id="dashboard" onContextMenu={(e) => e.preventDefault()}>
        {/* Show sidebar only when projects exist */}
        {log(`[Dashboard] Render check: initialCheckComplete=${initialCheckComplete()}, projectsFetchComplete=${projectsFetchComplete()}, projects=`, projects())}
        <Show when={initialCheckComplete() && projectsFetchComplete()}>
          <div id="sidebar">
            {log(`[Dashboard] Rendering Sidebar component`)}
            <Sidebar 
              setSidebarInitialized={setSidebarInitialized} 
              setComponent={setComponent} 
              fetchMenuTrigger={fetchMenuTrigger} 
              setFetchMenuTrigger={setFetchMenuTrigger}
              hasExistingDataset={hasExistingDataset()}
              openInSplitView={openInSplitView}
              isSplitView={isSplitView}
              updateLeftComponent={updateLeftComponent}
              rightPanelMenu={rightPanelMenu}
              closeSplitView={closeSplitView}
            />
          </div>
          <aside
            id="tags-explore-rail"
            class="tags-explore-rail"
            classList={{ "tags-explore-rail--open": hasValidSelectedProject() && tagsExploreRailOpen() }}
            aria-label="Tags"
            aria-hidden={!(hasValidSelectedProject() && tagsExploreRailOpen())}
          >
            <Show when={hasValidSelectedProject() && tagsExploreRailOpen()}>
              <TagsExplorePanel />
            </Show>
          </aside>
        </Show>

        <div id="main-content">
          {log(`[Dashboard] Rendering main-content, projectsFetchComplete=${projectsFetchComplete()}, hasValidSelectedProject=${hasValidSelectedProject()}, projects=`, projects())}
          <Show when={projectsFetchComplete() && !hasValidSelectedProject()}>
            <div class="dashboard-project-prompt">
              <Show when={!hasProjectInList()} fallback={<p>Select a project in the sidebar to continue.</p>}>
                <p>You do not have any projects yet. Use the project menu in the sidebar to add one.</p>
              </Show>
            </div>
          </Show>

          <Show when={hasValidSelectedProject()}>
            <Show when={showSelectionBanner()}>
              <SelectionBanner />
            </Show>
            <Show when={sidebarInitialized() && initialCheckComplete()}>
            <div class="playback-view-wrapper">
              <div
                class="workspace-shell"
                classList={{
                  "split-view-container": isSplitView(),
                  "workspace-shell--split": isSplitView(),
                }}
                style={`--workspace-primary-width: ${leftPanelWidth()}%; --workspace-secondary-width: ${100 - leftPanelWidth()}%;`}
              >
                <Show when={isSplitView() && isHoveringSplitView()}>
                  <div
                    class="split-view-floating-controls"
                    onMouseEnter={() => setIsHoveringSplitView(true)}
                    onMouseLeave={() => setIsHoveringSplitView(false)}
                  >
                    <button
                      class="split-view-close-btn"
                      onClick={closeSplitView}
                      title="Close split view"
                    >
                      ×
                    </button>
                  </div>
                </Show>
                <div class="workspace-row" classList={{ "split-view-content": isSplitView() }}>
                  <div
                    class="workspace-primary"
                    classList={{
                      "split-panel": isSplitView(),
                      "left-panel": isSplitView(),
                      "split-panel-with-playback": isSplitView(),
                    }}
                  >
                    <Dynamic
                      component={component() ?? EmptyMainView}
                      setFetchMenuTrigger={setFetchMenuTrigger}
                      isInSplitView={isSplitView()}
                      rightPanelMenu={rightPanelMenu()}
                    />
                  </div>
                  <Show when={isSplitView()}>
                    <div
                      class="split-divider"
                      onMouseDown={handleDragStart}
                      title="Drag to resize panels"
                    />
                    <div
                      class="split-panel right-panel workspace-secondary"
                      onMouseEnter={() => setIsHoveringSplitView(true)}
                      onMouseLeave={() => setIsHoveringSplitView(false)}
                    >
                      <Show when={rightComponent()}>
                        {(RightView) => (
                          <Dynamic
                            component={RightView()}
                            setFetchMenuTrigger={setFetchMenuTrigger}
                            isInSplitView={true}
                            mainPanelMenu={leftPanelMenu()}
                          />
                        )}
                      </Show>
                    </div>
                  </Show>
                </div>
              </div>
            </div>
          </Show>
          </Show>
          <CapitanFloatingButton
            openInSplitView={openInSplitView}
            isSplitView={isSplitView}
            rightPanelMenu={() => rightPanelMenu()}
          />
        </div>
      </div>
    </>
  );
}
